import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ParticlesBackground from "./ParticlesBackground";
import dashboardPreview from "@/assets/dashboard-preview.webp";
import eaulistikLogo from "@/assets/eaulistik-logo.webp";
import heroBg from "@/assets/hero-bg.webp";
import logoCluster from "@/assets/logos/cluster.webp";
import logoLeVillage from "@/assets/logos/levillage.webp";
import logoAquanova from "@/assets/logos/aquanova.webp";
import logoCapDigital from "@/assets/logos/cap-digital.webp";
import logoDauphine from "@/assets/logos/dauphine.webp";
import logoHecNetwork from "@/assets/logos/hec-network.webp";

const networkLogos = [
  { src: logoCluster, alt: "Cluster Eau Milieux Sols", className: "", url: "https://www.clusterems.org/" },
  { src: logoLeVillage, alt: "Le Village by CA", className: "", url: "https://paris.levillagebyca.com/" },
  { src: logoAquanova, alt: "Aquanova", className: "", url: "https://www.poleaquanova.fr/" },
  { src: logoCapDigital, alt: "Cap Digital", className: "", url: "https://www.capdigital.com/" },
  { src: logoDauphine, alt: "Dauphine PSL", className: "!h-4 md:!h-5 lg:!h-6", url: "https://dauphine.psl.eu/" },
  { src: logoHecNetwork, alt: "HEC Paris", className: "", url: "https://www.hec.edu/" },
];

interface HeroSectionProps {
  onDemoClick: () => void;
}

const HeroSection = ({ onDemoClick }: HeroSectionProps) => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll(".hero-animate");
    els?.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 0.15}s`;
      el.classList.add("animate-fade-up");
    });
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-[85vh] flex items-center pt-16 pb-32 md:pb-28 overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroBg} alt="" className="w-full h-full object-cover" loading="eager" width="1920" height="1080" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(216,56%,10%)]/95 via-[hsl(216,56%,10%)]/85 to-[hsl(216,56%,10%)]/40" />
      </div>
      <ParticlesBackground />

      <div className="container mx-auto px-4 grid lg:grid-cols-5 gap-12 items-center relative z-10">
        <div className="lg:col-span-3 space-y-6">
          <div className="hero-animate inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-xs font-body text-primary">
            {t("hero.badge")}
          </div>

          <img src={eaulistikLogo} alt="Eaulistik" className="hero-animate h-10 sm:h-12 lg:h-14 w-auto" width="400" height="98" />

          <h1 className="hero-animate font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.1] text-foreground">
            {t("hero.title1")}
            <br />
            <span className="text-gradient-cyan">{t("hero.title2")}</span>
          </h1>

          <p className="hero-animate text-lg text-muted-foreground max-w-xl font-body">
            {t("hero.desc")}
          </p>

          <div className="hero-animate flex flex-wrap items-center gap-4">
            <button
              onClick={onDemoClick}
              className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-body font-medium hover:glow-cyan-strong transition-all text-base"
            >
              {t("hero.cta")}
            </button>
            <button
              onClick={() => document.querySelector("#social-proof")?.scrollIntoView({ behavior: "smooth" })}
              className="text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors font-body text-sm"
            >
              {t("hero.caseStudy")}
            </button>
          </div>

          <div className="hero-animate flex gap-8 pt-4">
            {[
              { value: "-40%", label: t("hero.stat1") },
              { value: "-30%", label: t("hero.stat2") },
              { value: "-25%", label: t("hero.stat3") },
            ].map((s) => (
              <div key={s.value} className="text-center">
                <div className="font-mono text-2xl text-gold font-bold">{s.value}</div>
                <div className="text-xs text-muted-foreground font-body">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-animate lg:col-span-2 hidden lg:block">
          <div className="relative animate-float flex items-center justify-center mt-16">
            <img
              src={dashboardPreview}
              alt="Tableau de bord Eaulistik — monitoring temps réel des réseaux d'eau avec alertes fuites et indicateurs de consommation"
              className="w-[95%] max-w-none h-auto drop-shadow-2xl"
              width="1200"
              height="800"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute bottom-4 left-0 flex items-center gap-2 bg-card/90 backdrop-blur-sm border border-destructive/30 rounded-md p-2 shadow-lg scale-75 origin-bottom-left">
              <div className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
              <div>
                <div className="text-[11px] font-body font-medium text-foreground">{t("hero.alert")}</div>
                <div className="text-[9px] text-muted-foreground font-body">{t("hero.alertDesc")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 z-10 pointer-events-none">
        <div className="container mx-auto px-4">
          <div className="hidden md:flex flex-wrap items-center justify-start gap-6 lg:gap-10">
            <span className="text-xs font-body text-muted-foreground uppercase tracking-wider">{t("hero.network")}</span>
            {networkLogos.map((l) => (
              <a key={l.alt} href={l.url} target="_blank" rel="noopener noreferrer" className="pointer-events-auto">
                <img src={l.src} alt={l.alt} className={`h-8 md:h-10 lg:h-12 w-auto object-contain brightness-0 invert opacity-60 hover:opacity-90 transition-opacity ${l.className}`} loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
